/** Normalize therapist names for roster ↔ config matching. */
export const normalizeTherapistNameKey = (name) =>
  String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Match search text when first/last names are stored in either order (e.g. "Mahesh Shoraisham" vs "Shoraisham Mahesh"). */
export const therapistMatchesSearchTokens = (therapist, searchText) => {
  const search = String(searchText || '').trim().toLowerCase();
  if (!search) return true;
  const tokens = search.split(/\s+/).filter(Boolean);
  const haystack = [
    therapist?.name,
    therapist?.first_name,
    therapist?.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

export const resolveRosterDayAssignments = (rosterForDate, date) => {
  if (!rosterForDate || !date) return {};
  if (rosterForDate[date]) return rosterForDate[date];
  const target = String(date).trim();
  const matchKey = Object.keys(rosterForDate).find(
    (key) => String(key).trim() === target,
  );
  return matchKey ? rosterForDate[matchKey] : {};
};

/**
 * Collect therapist names rostered for a date from /api/roster/assignments payload.
 * By default excludes shifts marked UNAVAILABLE (leave / off-duty), matching Duty Roster.
 * Returns a Set of normalized name keys — use therapistMatchesRosterSet() to test.
 */
export const collectRosteredTherapistNames = (
  rosterForDate,
  date,
  { includeUnavailable = false } = {},
) => {
  const names = new Set();
  const dayAssignments = resolveRosterDayAssignments(rosterForDate, date);

  Object.values(dayAssignments).forEach((shiftData) => {
    if (!shiftData || typeof shiftData !== 'object') return;

    const availability = shiftData?.shift_details?.availability_type || 'AVAILABLE';
    if (!includeUnavailable && availability === 'UNAVAILABLE') return;

    (shiftData?.therapists || []).forEach((name) => {
      if (name && typeof name === 'string') {
        names.add(normalizeTherapistNameKey(name));
      }
    });
  });

  return names;
};

export const therapistMatchesRosterSet = (therapistName, rosterNameSet) => {
  if (!therapistName || !rosterNameSet) return false;
  return rosterNameSet.has(normalizeTherapistNameKey(therapistName));
};

export const therapistIsRosteredForDate = (
  therapistName,
  rosterForDate,
  date,
  options,
) => {
  if (!therapistName || !date) return false;
  const rostered = collectRosteredTherapistNames(rosterForDate, date, options);
  return therapistMatchesRosterSet(therapistName, rostered);
};

/** Active staff who may be scheduled on the given booking date (exit date respected). */
export const isTherapistSchedulableOnDate = (therapist, dateIso) => {
  if (!therapist) return false;
  if (therapist.is_active === false) return false;
  const exitRaw = therapist.exit_date;
  if (!exitRaw || !dateIso) return true;
  const exitDate = String(exitRaw).slice(0, 10);
  return dateIso <= exitDate;
};
